import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X, CloudUpload, Upload, Video, CheckCircle, AlertCircle, Sparkles, ImageIcon } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { apiRequest, getQueryFn } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import FiltersDrawer, { FilterState, getVideoFilterStyle } from "@/components/filters-drawer";
import VideoTextEditor from "@/components/video-text-editor";
import { DEFAULT_OVERLAY_STATE, type OverlayItem, type OverlayState } from "@scroll/shared-types/overlays";
import { composeVideo } from "@/lib/video-composer";
import { TimelineEditor, type TimelineEditorResult } from "@/components/timeline-editor";
import DraftsListSheet from "@/components/timeline-editor/DraftsListSheet";
import type { TimelineAutosaveOptions } from "@/components/timeline-editor/useTimelineEditor";
import type { PersistedTimelineProjectForWire } from "@/lib/timeline-serialize";
import type { TimelineProject } from "@scroll/shared-types/timeline";
import {
  buildComposeContextKey,
  getTimelineDraftService,
  type DraftSummary,
  type LocalBlobMap,
} from "@/lib/timeline-drafts";
import { loadFixtureProjectByName } from "@/lib/fixture-loader";
import {
  buildClientPublishPrerenderKey,
  createPublishSessionId,
  fallbackPublishPrerender,
  fetchPublishPrerender,
  fetchPublishPrerenderConfig,
  formatPublishPrerenderErrorMessage,
  getPublishPrerenderPostAction,
  shouldCompletePublishPrerenderSource,
  startPublishPrerender,
  uploadPrerenderSource,
  waitForPublishPrerenderSourceComplete,
  type PublishPrerenderRecord,
} from "@/lib/publish-prerender";
import ThumbnailFramePicker from "@/components/thumbnail-frame-picker";
import EpisodeUploadForm from "@/components/EpisodeUploadForm";
import {
  buildComposePostBody,
  buildVideoUploadMetadata,
  formatCount,
  shouldShowVideoUploadUpsell,
  VIDEO_UPLOAD_UPSELL_DISMISSED_KEY,
} from "@scroll/shared-utils";

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: "video" | "episode";
  /** Dev-only: when set (via ?timeline-fixture=<name>), auto-load the named
   *  fixture from /scripts/timeline-fixtures/ and open the editor with it.
   *  Used by the Step 9 preview-capture harness. Ignored in production
   *  because GlobalCreateFlow's readFixtureNameFromUrl returns null there. */
  fixtureName?: string | null;
}

const PUBLISH_PRERENDER_START_TIMEOUT_MS = 90_000;
const PUBLISH_PRERENDER_WAIT_TIMEOUT_MS = 180_000;

function waitForDelay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function getTimelineOverlayState(
  baseOverlayState: OverlayState,
  timelineProject: TimelineProject | undefined,
): OverlayState {
  if (!timelineProject?.textOverlays.length) return baseOverlayState;
  return {
    textOverlays: [
      ...baseOverlayState.textOverlays,
      ...timelineProject.textOverlays,
    ],
  };
}

export default function UploadModal({ isOpen, onClose, initialTab = "video", fixtureName = null }: UploadModalProps) {
  const [uploadTab, setUploadTab] = useState<"video" | "episode">(initialTab);
  const [description, setDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'processing' | 'thumbnail' | 'completed' | 'error'>('idle');
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const [uploadedPostId, setUploadedPostId] = useState<string | null>(null);
  const [uploadedPlaybackId, setUploadedPlaybackId] = useState<string | null>(null);
  const [uploadedDuration, setUploadedDuration] = useState<number>(30);
  const [videoThumbnail, setVideoThumbnail] = useState<string | null>(null);
  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
  const [filterState, setFilterState] = useState<FilterState>({ filterId: null, intensity: 100 });
  const [overlayState, setOverlayState] = useState<OverlayState>(DEFAULT_OVERLAY_STATE);
  const [composingProgress, setComposingProgress] = useState<number | null>(null);
  const [showTimelineEditor, setShowTimelineEditor] = useState(false);
  const [timelineProject, setTimelineProject] = useState<PersistedTimelineProjectForWire | null>(null);
  const [timelineEditorProject, setTimelineEditorProject] = useState<TimelineProject | undefined>();
  const [timelineSourceFile, setTimelineSourceFile] = useState<File | null>(null);
  const [timelineLinkOverlays, setTimelineLinkOverlays] = useState<OverlayItem[]>([]);
  const [videoMeta, setVideoMeta] = useState<{ duration: number; width: number; height: number } | null>(null);
  const [showProUpsell, setShowProUpsell] = useState(false);
  const [hashtagQuery, setHashtagQuery] = useState("");
  const [showHashtags, setShowHashtags] = useState(false);
  const [selectedHashtagIndex, setSelectedHashtagIndex] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // ─── Timeline draft persistence ──────────────────────────────────────
  // IndexedDB-backed draft service; the modal owns the compose context key
  // and the restore-side object URL lifecycle. See client/src/lib/timeline-drafts.ts.
  const draftService = useRef(getTimelineDraftService()).current;
  const [composeContextKey, setComposeContextKey] = useState<string | null>(null);
  const [hasResumableDraft, setHasResumableDraft] = useState(false);
  const restoredObjectUrlsRef = useRef<string[]>([]);
  const [draftSummaries, setDraftSummaries] = useState<DraftSummary[]>([]);
  const [showDraftsSheet, setShowDraftsSheet] = useState(false);
  // Stays true while we're seeding state from a draft-list restore. The
  // selectedFile-triggered context-key effect short-circuits while this is
  // set so it doesn't blow away the explicitly-set composeContextKey or
  // flicker `hasResumableDraft` back on through the parallel hasDraft check.
  const restoringFromListRef = useRef(false);
  // Set true in handleRestoreFromList instead of setShowTimelineEditor(true).
  // A separate effect waits for videoMeta + timelineEditorProject before
  // actually opening the editor, mirroring the dev-fixture path. Otherwise
  // the editor's render gate (which requires videoMeta) would silently fail.
  const [pendingDraftRestore, setPendingDraftRestore] = useState(false);

  // Refresh the drafts list. Invoked explicitly at modal open and after
  // anything that might have changed the set (editor close, delete from the
  // sheet, successful publish). Not a side-effect of state changes — the
  // previous `useEffect` dep on showTimelineEditor was firing on both edges
  // and producing an unnecessary IDB read every time the editor opened.
  const refreshDraftSummaries = useCallback(async () => {
    if (!user?.id) return;
    try {
      const all = await draftService.listDrafts(user.id);
      setDraftSummaries(all);
    } catch (err) {
      console.warn("[upload-modal] draft list failed", err);
    }
  }, [user?.id, draftService]);

  useEffect(() => {
    if (!isOpen) {
      setDraftSummaries([]);
      return;
    }
    void refreshDraftSummaries();
  }, [isOpen, refreshDraftSummaries]);

  // Refresh the drafts list when the editor closes (a save may have landed
  // in the meantime). Tracking the previous value of showTimelineEditor and
  // firing only on the true→false edge avoids the extra IDB read every time
  // the editor opens.
  const prevShowTimelineEditorRef = useRef(false);
  useEffect(() => {
    const wasOpen = prevShowTimelineEditorRef.current;
    prevShowTimelineEditorRef.current = showTimelineEditor;
    if (wasOpen && !showTimelineEditor) {
      void refreshDraftSummaries();
    }
  }, [showTimelineEditor, refreshDraftSummaries]);

  const revokeRestoredObjectUrls = useCallback(() => {
    for (const url of restoredObjectUrlsRef.current) {
      URL.revokeObjectURL(url);
    }
    restoredObjectUrlsRef.current = [];
  }, []);

  const { data: hashtagSuggestions = [] } = useQuery<{ id: number; name: string; postsCount: number }[]>({
    queryKey: ["/api/hashtags/autocomplete", hashtagQuery],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: showHashtags && hashtagQuery.length >= 1,
  });

  // Sync the tab to the requested initialTab only on the rising edge of isOpen.
  // Depending on initialTab as well would snap the tab mid-flow if a parent
  // changed the prop while the modal stayed open.
  useEffect(() => {
    if (isOpen) setUploadTab(initialTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Generate video preview URL when file is selected
  useEffect(() => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      setVideoPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setVideoPreviewUrl(null);
      setVideoThumbnail(null);
      setVideoMeta(null);
      setTimelineProject(null);
      setTimelineEditorProject(undefined);
      setTimelineSourceFile(null);
    }
  }, [selectedFile]);

  // Build the compose context key + peek for a resumable draft when a file is
  // chosen. The check is cheap: hasDraft does not read blob bytes or create
  // object URLs. Restore happens lazily when the user clicks the banner.
  useEffect(() => {
    // If we're in the middle of a draft-list restore, the caller already
    // set the correct composeContextKey and hasResumableDraft — bail before
    // the eager null reset and re-hash that would clobber them. The ref is
    // cleared by the restore handler once the editor is settled.
    if (restoringFromListRef.current) return;

    // Reset eagerly so autosave never fires against a stale key when the user
    // swaps files mid-session.
    setComposeContextKey(null);
    setHasResumableDraft(false);

    if (!selectedFile || !user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const key = await buildComposeContextKey({ userId: user.id, sourceFile: selectedFile });
        if (cancelled) return;
        setComposeContextKey(key);
        const exists = await draftService.hasDraft(key, user.id);
        if (cancelled) return;
        setHasResumableDraft(exists);
      } catch (err) {
        if (!cancelled) {
          console.warn("[upload-modal] draft lookup failed", err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedFile, user?.id, draftService]);

  // Generate thumbnail from video for filter preview
  useEffect(() => {
    if (videoPreviewUrl && videoRef.current) {
      const video = videoRef.current;
      const handleLoadedData = () => {
        // Seek to 1 second or 10% of video duration
        // Guard against NaN/Infinity duration (rare edge cases)
        const duration = video.duration;
        if (Number.isFinite(duration) && duration > 0) {
          video.currentTime = Math.min(1, duration * 0.1);
          setVideoMeta({ duration, width: video.videoWidth, height: video.videoHeight });
        } else {
          // Fallback: try seeking to 0 and capture first frame
          video.currentTime = 0;
        }
      };
      const handleSeeked = () => {
        // Create canvas and draw video frame
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          setVideoThumbnail(canvas.toDataURL("image/jpeg", 0.8));
        }
      };
      video.addEventListener("loadeddata", handleLoadedData);
      video.addEventListener("seeked", handleSeeked);
      return () => {
        video.removeEventListener("loadeddata", handleLoadedData);
        video.removeEventListener("seeked", handleSeeked);
      };
    }
  }, [videoPreviewUrl]);

  // Dev-only: when fixtureName is set (via GlobalCreateFlow reading
   // ?timeline-fixture=<name>), fetch the fixture JSON, pull the primary
   // clip's source as a File, and queue the editor to auto-open once
   // videoMeta loads. Used by the Step 9 preview-capture harness.
  const [pendingFixtureOpen, setPendingFixtureOpen] = useState(false);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!isOpen || !fixtureName) return;
    if (pendingFixtureOpen || timelineEditorProject || selectedFile) return;
    let cancelled = false;
    (async () => {
      const project = await loadFixtureProjectByName(fixtureName);
      if (!project || cancelled) return;
      const primary = project.clips[0];
      if (!primary) return;
      try {
        const res = await fetch(primary.sourceUri);
        if (!res.ok || cancelled) return;
        const blob = await res.blob();
        const file = new File([blob], `${fixtureName}.mp4`, {
          type: blob.type || "video/mp4",
        });
        if (cancelled) return;
        setSelectedFile(file);
        setTimelineEditorProject(project);
        setTimelineSourceFile(file);
        setPendingFixtureOpen(true);
      } catch (err) {
        if (!cancelled) {
          console.warn("[upload-modal] fixture source fetch failed", err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, fixtureName, pendingFixtureOpen, timelineEditorProject, selectedFile]);

  useEffect(() => {
    if (!pendingFixtureOpen) return;
    if (!videoMeta || !timelineEditorProject) return;
    setShowTimelineEditor(true);
    setPendingFixtureOpen(false);
  }, [pendingFixtureOpen, videoMeta, timelineEditorProject]);

  // Same gating pattern as the fixture path: the TimelineEditor render
  // condition is `showTimelineEditor && videoPreviewUrl && videoMeta &&
  // selectedFile`. videoPreviewUrl is created from selectedFile asynchronously
  // and videoMeta is populated from a video-element loadeddata callback, so we
  // can't flip showTimelineEditor in the same batch as setSelectedFile —
  // the editor would silently never render. Wait for the prerequisites here,
  // then clear restoringFromListRef once the editor's actually visible.
  useEffect(() => {
    if (!pendingDraftRestore) return;
    if (!videoMeta || !timelineEditorProject || !videoPreviewUrl) return;
    setShowTimelineEditor(true);
    setPendingDraftRestore(false);
    restoringFromListRef.current = false;
  }, [pendingDraftRestore, videoMeta, timelineEditorProject, videoPreviewUrl]);

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFile) {
        throw new Error("No file selected");
      }

      setIsUploading(true);
      setUploadStatus('uploading');
      setUploadProgress(0);

      // Merge timeline-editor text overlays into the base state ONCE and
      // use the merged set for both metadata serialization AND client-side
      // burn-in. Without this, a user who only added text overlays via
      // the timeline editor (and no other timeline-render-triggering edits)
      // would publish the raw video — burn-in checked the un-merged
      // overlayState, which doesn't include the editor's overlays.
      const effectiveOverlayState = getTimelineOverlayState(overlayState, timelineEditorProject);
      const metadata = buildVideoUploadMetadata({
        caption: description,
        filterState,
        overlayState: effectiveOverlayState,
      });

      if (timelineProject) {
        return publishTimelineProject({
          sourceFile: timelineSourceFile ?? selectedFile,
          timelineProject,
          metadata,
          linkOverlays: timelineLinkOverlays,
        });
      }

      // If text overlays exist, compose them into the video first
      let fileToUpload = selectedFile;
      if (effectiveOverlayState.textOverlays.length > 0) {
        setComposingProgress(0);
        setUploadStatus('processing');
        fileToUpload = await composeVideo(
          selectedFile,
          effectiveOverlayState.textOverlays,
          (p) => setComposingProgress(p)
        );
        setComposingProgress(null);
        setUploadStatus('uploading');
      }

      const formData = new FormData();
      formData.append('video', fileToUpload);
      formData.append('caption', metadata.caption);
      formData.append('hashtags', JSON.stringify(metadata.hashtags));
      if (metadata.filter) {
        formData.append('filter', JSON.stringify(metadata.filter));
      }
      if (metadata.overlays) {
        formData.append('overlays', JSON.stringify(metadata.overlays));
      }

      // Upload with progress tracking
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = (event.loaded / event.total) * 100;
            setUploadProgress(progress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            setUploadStatus('processing');
            resolve(JSON.parse(xhr.responseText));
          } else {
            reject(new Error('Upload failed'));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'));
        });

        xhr.open('POST', '/api/videos/upload');
        xhr.withCredentials = true;
        xhr.send(formData);
      });
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['/api/videos'] });
      queryClient.invalidateQueries({ queryKey: ['/api/feed'] });
      queryClient.invalidateQueries({ queryKey: ['/api/feed/following'] });
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: [`/api/users/${user.id}/posts`] });
      }

      // Successful publish clears the autosaved draft. Closing the modal via
      // X does NOT — the draft is preserved so the user can resume next time.
      if (composeContextKey) {
        draftService.discardDraft(composeContextKey).catch(() => {});
      }
      revokeRestoredObjectUrls();
      setHasResumableDraft(false);

      // Show Pro upsell for first-time uploaders who aren't Pro
      if (
        shouldShowVideoUploadUpsell({
          subscriptionTier: user?.subscriptionTier,
          dismissed: Boolean(localStorage.getItem(VIDEO_UPLOAD_UPSELL_DISMISSED_KEY)),
        })
      ) {
          setTimeout(() => setShowProUpsell(true), 1500);
      }

      if (data?.muxPlaybackId) {
        setUploadedPostId(data.postId || null);
        setUploadedPlaybackId(data.muxPlaybackId);
        setUploadedDuration(data.videoDuration || 30);
        setUploadStatus('thumbnail');
        setIsUploading(false);
      } else {
        setUploadStatus('completed');
        toast({
          title: "Video uploaded successfully!",
          description: "Your video is being processed and will be live shortly.",
        });
        resetAndClose();
      }
    },
    onError: (error) => {
      setUploadStatus('error');
      setIsUploading(false);
      toast({
        title: "Upload failed",
        description: error instanceof Error
          ? error.message
          : "Please try again with a different video file.",
        variant: "destructive",
      });
    },
  });

  const resetAndClose = () => {
    setTimeout(() => {
      setDescription("");
      setSelectedFile(null);
      setUploadProgress(0);
      setUploadStatus('idle');
      setIsUploading(false);
      setUploadedPostId(null);
      setUploadedPlaybackId(null);
      setUploadedDuration(30);
      setVideoMeta(null);
      setTimelineProject(null);
      setTimelineEditorProject(undefined);
      setTimelineSourceFile(null);
      setFilterState({ filterId: null, intensity: 100 });
      setOverlayState(DEFAULT_OVERLAY_STATE);
      setShowFiltersDrawer(false);
      setComposeContextKey(null);
      setHasResumableDraft(false);
      revokeRestoredObjectUrls();
      onClose();
    }, 2000);
  };

  const thumbnailMutation = useMutation({
    mutationFn: async (thumbnailTime: number) => {
      if (!uploadedPostId) throw new Error("No post ID");
      const res = await apiRequest("PATCH", `/api/posts/${uploadedPostId}/thumbnail`, { thumbnailTime });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/feed'] });
      queryClient.invalidateQueries({ queryKey: ['/api/videos'] });
      setUploadStatus('completed');
      toast({
        title: "Video uploaded successfully!",
        description: "Your thumbnail has been set.",
      });
      resetAndClose();
    },
  });

  const handleSkipThumbnail = () => {
    setUploadStatus('completed');
    toast({
      title: "Video uploaded successfully!",
      description: "Your video is being processed and will be live shortly.",
    });
    resetAndClose();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type.startsWith('video/')) {
        setSelectedFile(file);
        setTimelineProject(null);
        setTimelineEditorProject(undefined);
        setTimelineSourceFile(null);
        setUploadStatus('idle');
      } else {
        toast({
          title: "Invalid file type",
          description: "Please select a video file.",
          variant: "destructive",
        });
      }
    }
  };

  const handlePublish = () => {
    if (!selectedFile) {
      toast({
        title: "Video file required",
        description: "Please select a video file to upload.",
        variant: "destructive",
      });
      return;
    }

    if (!description.trim()) {
      toast({
        title: "Description required",
        description: "Please add a description for your video.",
        variant: "destructive",
      });
      return;
    }

    uploadMutation.mutate();
  };

  const handleTimelineDone = (result: TimelineEditorResult) => {
    setTimelineLinkOverlays(result.linkOverlays);
    if (result.persistedProject) {
      setTimelineProject(result.persistedProject);
      setTimelineEditorProject(result.project);
      setTimelineSourceFile(selectedFile);
      setShowTimelineEditor(false);
      return;
    }

    setTimelineProject(result.persistedProject);
    setTimelineEditorProject(result.project);
    setSelectedFile(result.exportedFile);
    setShowTimelineEditor(false);
  };

  const handleRestoreDraft = useCallback(async () => {
    if (!composeContextKey || !user?.id) return;
    try {
      const restored = await draftService.restoreDraft(composeContextKey, user.id);
      if (!restored) {
        setHasResumableDraft(false);
        return;
      }
      restoredObjectUrlsRef.current.push(...restored.objectUrls);
      setTimelineEditorProject(restored.project);
      setTimelineSourceFile(selectedFile);
      setHasResumableDraft(false);
      setShowTimelineEditor(true);
    } catch (err) {
      console.warn("[upload-modal] draft restore failed", err);
      toast({
        title: "Couldn't restore draft",
        description: "Starting a fresh timeline edit.",
        variant: "destructive",
      });
      setHasResumableDraft(false);
    }
  }, [composeContextKey, user?.id, selectedFile, draftService, toast]);

  // Restore-from-list path: the user picked a draft from the Drafts sheet
  // (not by re-selecting the source file). The restored draft surfaces a
  // synthesized File for the primary clip; we plug it into selectedFile so
  // autosave keeps working and the editor can boot from it.
  const handleRestoreFromList = useCallback(async (contextKey: string) => {
    if (!user?.id) return;
    try {
      const restored = await draftService.restoreDraft(contextKey, user.id);
      if (!restored) {
        console.warn("[upload-modal] restoreDraft returned null", { contextKey });
        toast({
          title: "Couldn't restore that draft",
          description: "The draft record is missing or doesn't belong to you.",
          variant: "destructive",
        });
        return;
      }
      if (!restored.primaryFile) {
        console.warn("[upload-modal] draft has no primary file blob", {
          contextKey,
          clips: restored.project.clips.map((c) => ({ id: c.id, sourceUri: c.sourceUri })),
        });
        toast({
          title: "Couldn't restore that draft",
          description: "The source video isn't in this draft. Re-pick the original file to resume.",
          variant: "destructive",
        });
        return;
      }

      // Revoke any URLs left over from a previous restore. Without this the
      // ref grows unbounded and stale entries in `autosaveOptions.localBlobs`
      // could shadow the live primary URL.
      revokeRestoredObjectUrls();

      // Signal the selectedFile effect to short-circuit so it doesn't blow
      // away the composeContextKey we're about to set, and doesn't flicker
      // hasResumableDraft back on. The flag is cleared by the
      // pendingDraftRestore effect once the editor is actually visible.
      restoringFromListRef.current = true;

      restoredObjectUrlsRef.current.push(...restored.objectUrls);
      setShowDraftsSheet(false);
      setComposeContextKey(contextKey);
      setHasResumableDraft(false);
      setTimelineSourceFile(restored.primaryFile);
      setTimelineEditorProject(restored.project);
      // setSelectedFile drives videoPreviewUrl creation + videoMeta resolution
      // via downstream effects; the pendingDraftRestore flag waits on those
      // before flipping showTimelineEditor so the editor doesn't render before
      // its prerequisites are ready.
      setSelectedFile(restored.primaryFile);
      setPendingDraftRestore(true);
    } catch (err) {
      console.warn("[upload-modal] draft restore-from-list failed", err);
      toast({
        title: "Couldn't restore draft",
        description: err instanceof Error ? err.message : "Try again or pick a different draft.",
        variant: "destructive",
      });
    }
  }, [user?.id, draftService, revokeRestoredObjectUrls, toast]);

  const handleDiscardDraft = useCallback(async () => {
    if (!composeContextKey) return;
    try {
      await draftService.discardDraft(composeContextKey);
    } catch (err) {
      console.warn("[upload-modal] draft discard failed", err);
    }
    revokeRestoredObjectUrls();
    setHasResumableDraft(false);
  }, [composeContextKey, draftService, revokeRestoredObjectUrls]);

  // Autosave wiring for the timeline editor. Only enabled once we have a
  // signed-in user, a source File, a built context key, and a video preview
  // URL — otherwise localBlobs has nothing to point at.
  const autosaveOptions = useMemo<TimelineAutosaveOptions | undefined>(() => {
    if (!composeContextKey || !user?.id || !selectedFile) return undefined;
    const userId = user.id;
    return {
      service: draftService,
      getConfig: () => {
        const localBlobs: LocalBlobMap = {};
        // The primary clip's sourceUri is either the modal's current
        // videoPreviewUrl (fresh selection) or one of the restore-side URLs
        // (resumed draft). Mapping both is safe: TimelineDraftService.saveDraft
        // ignores entries whose URI isn't actually in the project.
        if (videoPreviewUrl) {
          localBlobs[videoPreviewUrl] = {
            blob: selectedFile,
            originalName: selectedFile.name,
          };
        }
        for (const url of restoredObjectUrlsRef.current) {
          localBlobs[url] = {
            blob: selectedFile,
            originalName: selectedFile.name,
          };
        }
        return { contextKey: composeContextKey, userId, localBlobs };
      },
      onQuotaError: () => {
        toast({
          title: "Storage full",
          description: "Autosave is paused — your draft may not persist if you refresh.",
          variant: "destructive",
        });
      },
    };
  }, [composeContextKey, user?.id, selectedFile, videoPreviewUrl, draftService, toast]);

  if (!isOpen) return null;

  async function withTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number,
    message: string,
  ): Promise<T> {
    let timeout: number | undefined;
    try {
      return await Promise.race([
        promise,
        new Promise<T>((_resolve, reject) => {
          timeout = window.setTimeout(() => reject(new Error(message)), timeoutMs);
        }),
      ]);
    } finally {
      if (timeout !== undefined) window.clearTimeout(timeout);
    }
  }

  async function publishTimelineProject({
    sourceFile,
    timelineProject,
    metadata,
    linkOverlays = [],
  }: {
    sourceFile: File;
    timelineProject: PersistedTimelineProjectForWire;
    metadata: ReturnType<typeof buildVideoUploadMetadata>;
    linkOverlays?: OverlayItem[];
  }) {
    const config = await fetchPublishPrerenderConfig();
    if (!config.enabled) {
      throw new Error("Video rendering is temporarily unavailable. Please try again later.");
    }

    const publishSessionId = createPublishSessionId();
    const voiceover = null;
    const renderKey = buildClientPublishPrerenderKey({
      timelineProject,
      linkOverlays,
      voiceover,
      applyWatermark: false,
    });

    const publishStartedAt = Date.now();
    let { prerender, uploadUrl } = await withTimeout(
      startPublishPrerender({
        publishSessionId,
        renderKey,
        timelineProject,
        linkOverlays,
        voiceover,
        applyWatermark: false,
      }),
      PUBLISH_PRERENDER_START_TIMEOUT_MS,
      "Starting the final video render is taking too long. Please try again.",
    );
    console.info("[timeline-publish] prerender queued", {
      prerenderId: prerender.id,
      status: prerender.status,
      hasUploadUrl: Boolean(uploadUrl),
      clipCount: timelineProject.clips.length,
      transitionCount: timelineProject.transitions.length,
      audioTrackCount: timelineProject.audioTracks.length,
    });

    if (prerender.status === "uploading" && !uploadUrl) {
      await fallbackPublishPrerender(prerender.id);
      const restarted = await startPublishPrerender({
        publishSessionId,
        renderKey,
        timelineProject,
        linkOverlays,
        voiceover,
        applyWatermark: false,
      });
      prerender = restarted.prerender;
      uploadUrl = restarted.uploadUrl;
    }

    if (prerender.status === "uploading" && !uploadUrl) {
      throw new Error("Unable to restart the final video upload. Please try publishing again.");
    }

    if (uploadUrl) {
      await uploadPrerenderSource({
        uploadUrl,
        file: sourceFile,
        onProgress: setUploadProgress,
      });
    }

    if (shouldCompletePublishPrerenderSource(prerender.status)) {
      const sourceCompleteResult = await waitForPublishPrerenderSourceComplete(prerender.id);
      prerender = sourceCompleteResult.prerender;
    }

    setUploadStatus("processing");
    const readyPrerender = await waitForPublishPrerenderReady(prerender);
    console.info("[timeline-publish] prerender ready", {
      prerenderId: readyPrerender.id,
      elapsedMs: Date.now() - publishStartedAt,
    });
    setUploadProgress(100);
    setUploadStatus("uploading");

    const postRes = await apiRequest(
      "POST",
      "/api/posts",
      buildComposePostBody({
        content: description,
        replyPermission: "everyone",
        timezoneOffset: new Date().getTimezoneOffset(),
        extra: {
          timelineProject,
          prerenderId: readyPrerender.id,
          prerenderRenderKey: readyPrerender.renderKey,
          ...(metadata.filter ? { filter: metadata.filter } : {}),
          ...(metadata.overlays ? { overlays: metadata.overlays } : {}),
        },
      }),
    );

    if (!postRes.ok) {
      const body = await postRes.json().catch(() => null) as { error?: string; message?: string } | null;
      throw new Error(body?.message || body?.error || "Failed to create post");
    }

    console.info("[timeline-publish] post created", {
      prerenderId: readyPrerender.id,
      totalElapsedMs: Date.now() - publishStartedAt,
    });
    return postRes.json();
  }

  async function waitForPublishPrerenderReady(
    initialPrerender: PublishPrerenderRecord,
  ): Promise<PublishPrerenderRecord> {
    let latest = initialPrerender;
    const waitStartedAt = Date.now();

    while (true) {
      const action = getPublishPrerenderPostAction(latest.status);
      if (action === "publish-rendered") return latest;
      if (action === "error") {
        console.warn("[timeline-publish] prerender failed", {
          prerenderId: latest.id,
          status: latest.status,
          errorMessage: latest.errorMessage,
          elapsedMs: Date.now() - waitStartedAt,
        });
        throw new Error(formatPublishPrerenderErrorMessage(latest.errorMessage));
      }
      if (Date.now() - waitStartedAt > PUBLISH_PRERENDER_WAIT_TIMEOUT_MS) {
        console.warn("[timeline-publish] prerender timed out", {
          prerenderId: latest.id,
          status: latest.status,
          elapsedMs: Date.now() - waitStartedAt,
        });
        await fallbackPublishPrerender(latest.id).catch(() => {});
        throw new Error("Rendering is taking longer than expected. Please cancel and try again in a minute.");
      }
      await waitForDelay(2000);
      const { prerender } = await fetchPublishPrerender(latest.id);
      latest = prerender;
    }
  }

  const handleDescriptionChange = (value: string, cursorPos: number) => {
    setDescription(value);
    const textBefore = value.slice(0, cursorPos);
    const match = textBefore.match(/#(\w*)$/);
    if (match) {
      setHashtagQuery(match[1]);
      setShowHashtags(true);
      setSelectedHashtagIndex(0);
      return;
    }
    setHashtagQuery("");
    setShowHashtags(false);
  };

  const selectHashtag = (tagName: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const cursorPos = textarea.selectionStart ?? description.length;
    const textBefore = description.slice(0, cursorPos);
    const match = textBefore.match(/#(\w*)$/);
    if (!match) return;

    const start = cursorPos - match[0].length;
    const nextDescription =
      description.slice(0, start) + `#${tagName} ` + description.slice(cursorPos);

    setDescription(nextDescription);
    setHashtagQuery("");
    setShowHashtags(false);
    setSelectedHashtagIndex(0);

    setTimeout(() => {
      const nextPos = start + tagName.length + 2;
      textarea.selectionStart = textarea.selectionEnd = nextPos;
      textarea.focus();
    }, 0);
  };

  const getStatusIcon = () => {
    switch (uploadStatus) {
      case 'uploading':
        return <Upload className="animate-spin" size={20} />;
      case 'processing':
        return <Video className="animate-pulse" size={20} />;
      case 'thumbnail':
        return <ImageIcon className="text-primary" size={20} />;
      case 'completed':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'error':
        return <AlertCircle className="text-red-500" size={20} />;
      default:
        return null;
    }
  };

  const getStatusText = () => {
    switch (uploadStatus) {
      case 'uploading':
        return `Uploading... ${Math.round(uploadProgress)}%`;
      case 'processing':
        return composingProgress !== null
          ? `Composing text overlays... ${composingProgress}%`
          : 'Processing video...';
      case 'thumbnail':
        return 'Choose a thumbnail';
      case 'completed':
        return 'Upload completed!';
      case 'error':
        return 'Upload failed';
      default:
        return '';
    }
  };

  return (
    <div className="fixed inset-0 bg-(--scroll-bg) z-50 overflow-y-auto">
      <div className="p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))] min-h-full">
        <div className="flex items-center justify-between mb-4">
          <button className="text-(--scroll-text)" onClick={onClose} disabled={isUploading}>
            <X size={20} />
          </button>
          <h2 className="scroll-heading text-lg">Upload</h2>
          {uploadTab === "video" ? (
            <Button
              className="scroll-btn-primary disabled:opacity-50"
              onClick={handlePublish}
              disabled={isUploading || !selectedFile}
            >
              {isUploading ? "Uploading..." : "Publish"}
            </Button>
          ) : (
            <div className="w-[72px]" />
          )}
        </div>

        {/* Tab toggle */}
        <div className="flex gap-1 mb-6">
          {(["video", "episode"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setUploadTab(tab)}
              disabled={isUploading}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors capitalize disabled:opacity-50 ${
                uploadTab === tab
                  ? "bg-(--scroll-text) text-(--scroll-bg)"
                  : "text-(--scroll-text-secondary) hover:bg-(--scroll-bg-secondary)"
              }`}
            >
              {tab === "video" ? "Video" : "Episode"}
            </button>
          ))}
        </div>

        {/* Episode tab */}
        {uploadTab === "episode" && <EpisodeUploadForm onClose={onClose} />}

        {/* Video tab content */}
        {uploadTab === "video" && <>

        {/* Video preview area */}
        {videoPreviewUrl ? (
          <div className="relative mb-6">
            {/* Video preview with filter applied + text overlays */}
            <div
              ref={videoContainerRef}
              className="relative aspect-9/16 max-h-[40vh] mx-auto rounded-xl overflow-hidden bg-black"
            >
              <video
                ref={videoRef}
                src={videoPreviewUrl}
                className="w-full h-full object-contain"
                style={{ filter: getVideoFilterStyle(filterState) }}
                autoPlay
                loop
                muted
                playsInline
              />
              {/* Text overlay editor */}
              <VideoTextEditor
                overlayState={overlayState}
                onOverlayChange={setOverlayState}
                containerRef={videoContainerRef}
              />
              {/* Change video button */}
              <label
                aria-label="Change video"
                className={`absolute top-3 right-3 bg-black/50 rounded-full p-2 text-white hover:bg-black/70 transition-colors z-20 ${
                  isUploading ? "pointer-events-none opacity-50" : "cursor-pointer"
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  aria-label="Change video"
                  onChange={handleFileSelect}
                  className="sr-only"
                  disabled={isUploading}
                />
                <X size={16} />
              </label>
            </div>

            {/* Composing progress */}
            {composingProgress !== null && (
              <div className="mt-3 px-4">
                <p className="text-(--scroll-text-secondary) text-xs text-center mb-1">
                  Burning text into video... {composingProgress}%
                </p>
                <Progress value={composingProgress} className="h-1.5" />
              </div>
            )}

            {/* Resume previous edit banner — only shown when a saved draft
                matches this exact source file's content key. Closing the
                modal via X intentionally preserves the draft; only Discard
                or a successful publish removes it. */}
            {hasResumableDraft && !showTimelineEditor && !timelineProject && (
              <div
                role="status"
                className="mt-3 mx-4 flex items-center justify-between gap-3 rounded-lg border border-(--scroll-border) bg-(--scroll-bg-secondary) px-3 py-2 text-sm"
              >
                <span className="text-(--scroll-text)">
                  Resume your previous edit on this video?
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleRestoreDraft}
                    disabled={isUploading}
                    className="rounded-full bg-(--scroll-text) text-(--scroll-bg) px-3 py-1 text-xs font-medium disabled:opacity-50"
                  >
                    Resume
                  </button>
                  <button
                    type="button"
                    onClick={handleDiscardDraft}
                    disabled={isUploading}
                    className="rounded-full px-3 py-1 text-xs text-(--scroll-text-secondary) hover:text-(--scroll-text) disabled:opacity-50"
                  >
                    Discard
                  </button>
                </div>
              </div>
            )}

            {/* Editor buttons */}
            <div className="flex items-center justify-center gap-2 mt-3">
              <button
                onClick={() => setShowFiltersDrawer(true)}
                disabled={isUploading}
                className="flex items-center gap-2 px-4 py-2 bg-(--scroll-bg-secondary) hover:bg-(--scroll-bg-tertiary) rounded-full text-(--scroll-text) text-sm transition-colors disabled:opacity-50"
              >
                <Sparkles size={16} />
                {filterState.filterId ? "Change Filter" : "Add Filter"}
              </button>
              {videoMeta && (
                <button
                  onClick={() => setShowTimelineEditor(true)}
                  disabled={isUploading}
                  className="flex items-center gap-2 px-4 py-2 bg-(--scroll-bg-secondary) hover:bg-(--scroll-bg-tertiary) rounded-full text-(--scroll-text) text-sm transition-colors disabled:opacity-50"
                >
                  <Video size={16} />
                  {timelineProject ? "Edit Timeline" : "Advanced Editor"}
                </button>
              )}
            </div>
          </div>
        ) : (
          /* Upload area */
          <>
            <label
              className="relative block border-2 border-dashed rounded-xl p-8 text-center mb-3 cursor-pointer transition-colors border-(--scroll-border) hover:border-(--scroll-text-tertiary) focus-within:border-(--scroll-text-tertiary)"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleFileSelect}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                disabled={isUploading}
              />
              <CloudUpload className="pointer-events-none text-(--scroll-text-tertiary) mx-auto mb-4" size={48} />
              <p className="pointer-events-none text-(--scroll-text-secondary) mb-2">Click to select video file</p>
              <p className="pointer-events-none text-(--scroll-text-tertiary) text-sm">Max file size: 100MB</p>
            </label>

            {draftSummaries.length > 0 && (
              <div className="mb-6 flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => setShowDraftsSheet(true)}
                  className="text-sm text-(--scroll-text-secondary) hover:text-(--scroll-text) underline-offset-4 hover:underline"
                >
                  Resume a saved draft ({draftSummaries.length})
                </button>
              </div>
            )}
          </>
        )}

        {/* Upload progress */}
        {(uploadStatus !== 'idle') && (
          <div className="mb-6 p-4 bg-(--scroll-bg-secondary) rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              {getStatusIcon()}
              <span className="text-(--scroll-text) font-medium">{getStatusText()}</span>
            </div>
            {uploadStatus === 'uploading' && (
              <Progress value={uploadProgress} className="h-2" />
            )}
          </div>
        )}

        {/* Thumbnail picker step */}
        {uploadStatus === 'thumbnail' && uploadedPlaybackId && (
          <div className="mb-6 space-y-3">
            <ThumbnailFramePicker
              playbackId={uploadedPlaybackId}
              duration={uploadedDuration}
              onSelect={(time) => thumbnailMutation.mutate(time)}
              isLoading={thumbnailMutation.isPending}
            />
            <button
              onClick={handleSkipThumbnail}
              className="block mx-auto text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Skip
            </button>
          </div>
        )}

        {/* Upload form */}
        <div className="space-y-4">
          <div>
            <label className="text-(--scroll-text) font-medium block mb-2">Description</label>
            <div className="relative">
              <Textarea
                ref={textareaRef}
                placeholder="Write a caption with #hashtags..."
                value={description}
                onChange={(e) =>
                  handleDescriptionChange(
                    e.target.value,
                    e.target.selectionStart ?? e.target.value.length,
                  )
                }
                onSelect={(e) => {
                  // Cursor moves only dismiss the dropdown — typing (onChange) is what opens it.
                  // Re-running the full handler here would re-trigger suggestion queries on every click.
                  const target = e.target as HTMLTextAreaElement;
                  const cursorPos = target.selectionStart ?? target.value.length;
                  if (showHashtags && !target.value.slice(0, cursorPos).match(/#(\w*)$/)) {
                    setHashtagQuery("");
                    setShowHashtags(false);
                  }
                }}
                onKeyDown={(e) => {
                  if (showHashtags && hashtagSuggestions.length > 0) {
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setSelectedHashtagIndex((i) => Math.min(i + 1, hashtagSuggestions.length - 1));
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setSelectedHashtagIndex((i) => Math.max(i - 1, 0));
                    } else if (e.key === "Enter") {
                      e.preventDefault();
                      selectHashtag(hashtagSuggestions[selectedHashtagIndex].name);
                    } else if (e.key === "Escape") {
                      setShowHashtags(false);
                    }
                  }
                }}
                className="scroll-input border resize-none h-24"
                disabled={isUploading}
              />
              {showHashtags && hashtagSuggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1 bg-(--scroll-bg) border border-(--scroll-border) rounded-lg shadow-lg overflow-hidden">
                  {hashtagSuggestions.map((tag, index) => (
                    <button
                      key={tag.id}
                      type="button"
                      className={`w-full text-left px-3 py-2 flex items-center justify-between text-sm transition-colors ${
                        index === selectedHashtagIndex ? "bg-(--scroll-bg-secondary)" : "hover:bg-(--scroll-bg-secondary)"
                      }`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectHashtag(tag.name);
                      }}
                    >
                      <span className="text-[#1D9BF0] font-medium">#{tag.name}</span>
                      <span className="text-(--sl-text-tertiary) text-xs">{formatCount(tag.postsCount)} posts</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        </>}
      </div>

      {/* Filters Drawer */}
      <FiltersDrawer
        isOpen={showFiltersDrawer}
        onClose={() => setShowFiltersDrawer(false)}
        selectedFilter={filterState}
        onFilterChange={setFilterState}
        videoThumbnail={videoThumbnail || undefined}
      />

      {/* Backdrop for filters drawer */}
      {showFiltersDrawer && (
        <div
          className="fixed inset-0 z-55 scroll-modal-overlay"
          onClick={() => setShowFiltersDrawer(false)}
        />
      )}

      {/* Timeline Editor */}
      {showTimelineEditor && videoPreviewUrl && videoMeta && selectedFile && (
        <TimelineEditor
          videoSrc={videoPreviewUrl}
          videoFile={selectedFile}
          videoDuration={videoMeta.duration}
          videoWidth={videoMeta.width}
          videoHeight={videoMeta.height}
          initialProject={timelineEditorProject}
          autosave={autosaveOptions}
          onDone={handleTimelineDone}
          onClose={() => setShowTimelineEditor(false)}
        />
      )}

      {/* Pro upsell overlay */}
      {showProUpsell && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-card rounded-xl p-6 max-w-sm mx-4 text-center space-y-4">
            <h3 className="text-lg font-semibold">Your video is live!</h3>
            <p className="text-sm text-muted-foreground">
              Want to see how it performs? Try Scroll Pro free for 7 days — get analytics, longer videos, and more.
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  localStorage.setItem(VIDEO_UPLOAD_UPSELL_DISMISSED_KEY, "true");
                  setShowProUpsell(false);
                }}
              >
                Maybe later
              </Button>
              <Button
                className="flex-1"
                onClick={() => {
                  localStorage.setItem(VIDEO_UPLOAD_UPSELL_DISMISSED_KEY, "true");
                  setShowProUpsell(false);
                  window.location.href = "/settings/scroll-pro";
                }}
              >
                Try Pro Free
              </Button>
            </div>
          </div>
        </div>
      )}

      {showDraftsSheet && user?.id && (
        <DraftsListSheet
          service={draftService}
          userId={user.id}
          onClose={() => setShowDraftsSheet(false)}
          onRestore={handleRestoreFromList}
        />
      )}
    </div>
  );
}
